/**
 * Alshallal Pastry Company HR System - Main Application
 * Phase 4: Leave Management
 * Phase 5: Permission-based UI Gating
 * Phase 18.10: Module Visibility Controls with Route Guards
 */
import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

// Contexts
import { AuthProvider } from "./contexts/AuthContext";
import { LanguageProvider } from "./contexts/LanguageContext";
import { ModulePolicyProvider } from "./contexts/ModulePolicyContext";
import { PermissionProvider } from "./contexts/PermissionContext";

// Layout
import { MainLayout } from "./components/layout";

// Module Guard for route-level protection
import ModuleGuard from "./components/ModuleGuard";
import WarningDetailsPage from "./pages/warnings/WarningDetailsPage";

// Session Warning
import SessionTimeoutWarning from "./components/SessionTimeoutWarning";

// Pages
import {
  LoginPage,
  ChangePasswordPage,
  NdaConsentPage,
  DashboardPage,
  AuditLogsPage,
  EmployeesListPage,
  EmployeeCreatePage,
  EmployeeDetailsPage,
  EmployeeEditPage,
  MyDocumentsPage,
  HRDocumentVerificationPage,
  DocumentDetailsPage,
  CompanyDocumentsPage,  // Phase 19
  MyLeavePage,
  LeaveApprovalsPage,
  LeaveAdminPage,
  TeamLeavePage,
  UnexcusedAbsencePage,
  MyRequestsPage,
  RequestApprovalsPage,
  RequestsAdminPage,
  AdminConsolePage,
  AdminMonitoringPage,
  OrgChartPage,
  SettingsPage,
  CustomFieldsPage,  // Phase 18.8.1
  AccessRolesPage,   // Phase 18.9: User management under Settings
  ModuleVisibilityPage,  // Phase 18.10: Module visibility controls
  NotificationsPage,  // Phase 20: Email Broadcast Notifications
  LetterTemplatesPage,  // Phase T1: Letter Template Management
  DocumentTemplatesPage,  // Phase D1: Document Template Management
  MyLettersPage,  // Phase 25: Letters
  HRLettersPage,  // Phase 25: Letters
  LettersCompanySettingsPage,  // Phase 25: Letters
  AuthorityPage,  // Phase P1: HR Authority Dashboard
  ProfilePage,
  NotFoundPage,
} from "./pages";
import { SystemPage, SystemResetPage, OCRAnalyticsPage, StorageHealthPage, TwoStepSignInPage, ConfidentialityUndertakingPage, IdentifierFormatsPage } from "./pages/system";
import { ImportPage } from "./pages/import";
import { DepartmentsPage } from "./pages/departments";
import { BranchesPage } from "./pages/branches";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import ExpiringDocumentsPage from "./pages/admin/ExpiringDocumentsPage";  // Phase 19
import PendingEmployeeUpdatesPage from "./pages/admin/PendingEmployeeUpdatesPage";  // Phase D2
import EmailHealthPage from "./pages/admin/EmailHealthPage";  // Phase E1.4
import MyProfilePage from "./pages/MyProfilePage";  // My Profile feature
import RepositoryPage from "./pages/settings/RepositoryPage";  // Phase 21: Archive Repository
import AIInboxPage from "./pages/settings/AIInboxPage";  // Phase 23: AI Inbox
import QCViolationsPage from "./pages/qc/QCViolationsPage";  // QC Integration
import AttendancePage from "./pages/attendance/AttendancePage";  // Attendance (fingerprint)
import MyAttendancePage from "./pages/attendance/MyAttendancePage";  // Attendance: my approved month
import AdminMaintenancePage from "./pages/admin/AdminMaintenancePage";  // Data-quality tools

import "./index.css";

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <PermissionProvider>
          <ModulePolicyProvider>
            <BrowserRouter>
              {/* Session Timeout Warning for HR Admin/Super Admin */}
              <SessionTimeoutWarning />
            
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/change-password" element={<ChangePasswordPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />

              {/* Mandatory Confidentiality Undertaking. Deliberately OUTSIDE
                  MainLayout: no sidebar, no nav, nothing to click past. The
                  server refuses every other endpoint until it is accepted. */}
              <Route path="/nda" element={<NdaConsentPage />} />
              
              {/* Protected routes with MainLayout */}
              <Route element={<MainLayout />}>
                <Route path="/dashboard" element={<ModuleGuard moduleKey="dashboard"><DashboardPage /></ModuleGuard>} />
                {/* My Profile - all authenticated users (no guard needed) */}
                <Route path="/my-profile" element={<MyProfilePage />} />
              {/* Employee routes */}
              <Route path="/employees" element={<ModuleGuard moduleKey="employees"><EmployeesListPage /></ModuleGuard>} />
              <Route path="/employees/new" element={<ModuleGuard moduleKey="employees"><EmployeeCreatePage /></ModuleGuard>} />
              <Route path="/employees/create" element={<Navigate to="/employees/new" replace />} />
              <Route path="/employees/:id" element={<ModuleGuard moduleKey="employees"><EmployeeDetailsPage /></ModuleGuard>} />
              <Route path="/employees/:id/edit" element={<ModuleGuard moduleKey="employees"><EmployeeEditPage /></ModuleGuard>} />
              {/* Backward compatibility: Redirect /users to /settings/access */}
              <Route path="/users" element={<Navigate to="/settings/access" replace />} />
              {/* Document routes */}
              <Route path="/documents" element={<ModuleGuard moduleKey="documents"><MyDocumentsPage /></ModuleGuard>} />
              <Route path="/documents/my" element={<ModuleGuard moduleKey="documents"><MyDocumentsPage /></ModuleGuard>} />
              <Route path="/documents/verification" element={<ModuleGuard moduleKey="documents_verification"><HRDocumentVerificationPage /></ModuleGuard>} />
              <Route path="/documents/company" element={<ModuleGuard moduleKey="documents"><CompanyDocumentsPage /></ModuleGuard>} />  {/* Phase 19 */}
              <Route path="/documents/:id" element={<ModuleGuard moduleKey="documents"><DocumentDetailsPage /></ModuleGuard>} />
              {/* Warning letter by reference: the server decides who may see it */}
              <Route path="/warnings/:ref" element={<WarningDetailsPage />} />
              {/* Leave routes */}
              <Route path="/leave" element={<ModuleGuard moduleKey="leave"><MyLeavePage /></ModuleGuard>} />
              <Route path="/leave/my" element={<ModuleGuard moduleKey="leave"><MyLeavePage /></ModuleGuard>} />
              <Route path="/leave/approvals" element={<ModuleGuard moduleKey="leave_approvals"><LeaveApprovalsPage /></ModuleGuard>} />
              <Route path="/leave/admin" element={<ModuleGuard moduleKey="leave_admin"><LeaveAdminPage /></ModuleGuard>} />
              <Route path="/leave/team" element={<ModuleGuard moduleKey="leave_approvals"><TeamLeavePage /></ModuleGuard>} />
              <Route path="/leave/unexcused-absence" element={<ModuleGuard moduleKey="leave_approvals"><UnexcusedAbsencePage /></ModuleGuard>} />
              {/* Request routes */}
              <Route path="/requests" element={<ModuleGuard moduleKey="requests"><MyRequestsPage /></ModuleGuard>} />
              <Route path="/requests/my" element={<ModuleGuard moduleKey="requests"><MyRequestsPage /></ModuleGuard>} />
              <Route path="/requests/approvals" element={<ModuleGuard moduleKey="requests_approvals"><RequestApprovalsPage /></ModuleGuard>} />
              <Route path="/requests/admin" element={<ModuleGuard moduleKey="requests_admin"><RequestsAdminPage /></ModuleGuard>} />
              {/* Import routes - Phase 13 */}
              <Route path="/import" element={<ModuleGuard moduleKey="import"><ImportPage /></ModuleGuard>} />
              {/* Departments routes - Phase 18.3.1 */}
              <Route path="/departments" element={<ModuleGuard moduleKey="employees"><DepartmentsPage /></ModuleGuard>} />
              {/* Branches moved to /settings/branches - Phase 24.9 */}
              {/* Org Chart routes - Phase 18.4.1 */}
              <Route path="/org-chart" element={<ModuleGuard moduleKey="org_chart"><OrgChartPage /></ModuleGuard>} />
              {/* Letters routes - Phase 25 */}
              <Route path="/letters" element={<MyLettersPage />} />
              <Route path="/letters/my" element={<MyLettersPage />} />
              <Route path="/letters/hr" element={<ModuleGuard moduleKey="employees"><HRLettersPage /></ModuleGuard>} />
              <Route path="/letters/settings" element={<ModuleGuard moduleKey="admin_console"><LettersCompanySettingsPage /></ModuleGuard>} />
              {/* Phase 19: Expiring Documents Report (HR only) */}
              <Route path="/admin/documents/expiring" element={<ModuleGuard moduleKey="admin_console"><ExpiringDocumentsPage /></ModuleGuard>} />
              <Route path="/admin/pending-updates" element={<ModuleGuard moduleKey="admin_console"><PendingEmployeeUpdatesPage /></ModuleGuard>} />  {/* Phase D2 */}
              {/* Phase P1: HR Authority Dashboard (permission-gated, no module guard needed) */}
              <Route path="/authority" element={<AuthorityPage />} />
              {/* QC Violations (HR review queue for ops.shallal.me integration) */}
              <Route path="/qc-violations" element={<QCViolationsPage />} />
              {/* Attendance (fingerprint devices via the office relay) */}
              <Route path="/attendance" element={<ModuleGuard moduleKey="attendance"><AttendancePage /></ModuleGuard>} />
              <Route path="/attendance/my" element={<ModuleGuard moduleKey="attendance"><MyAttendancePage /></ModuleGuard>} />
              <Route path="/admin/maintenance" element={<AdminMaintenancePage />} />
              {/* System with nested routes - Phase 24.9: Audit, Admin Console, Monitoring, System Reset */}
              <Route path="/system" element={<SystemPage />}>
                <Route path="audit" element={<ModuleGuard moduleKey="audit"><AuditLogsPage /></ModuleGuard>} />
                <Route path="email-health" element={<EmailHealthPage />} />  {/* Phase E1.4 - moved to system */}
                <Route path="admin" element={<ModuleGuard moduleKey="admin_console"><AdminConsolePage /></ModuleGuard>} />
                <Route path="monitoring" element={<ModuleGuard moduleKey="monitoring"><AdminMonitoringPage /></ModuleGuard>} />
                <Route path="ocr-analytics" element={<OCRAnalyticsPage />} />  {/* Phase 1.5 */}
                <Route path="storage-health" element={<StorageHealthPage />} />  {/* Storage durability + broken-file re-upload */}
                <Route path="two-step" element={<TwoStepSignInPage />} />  {/* Gradual OTP rollout - Super Admin only */}
                <Route path="nda" element={<ConfidentialityUndertakingPage />} />  {/* Confidentiality Undertaking rollout - Super Admin only */}
                <Route path="id-formats" element={<IdentifierFormatsPage />} />  {/* How many identifiers are stored as numbers - read-only */}
                <Route path="reset" element={<SystemResetPage />} />
              </Route>
              {/* Settings with nested routes - Phase 18.8.1, 18.9, 18.10, 20, 21 & 24.9, T1, D1 */}
              <Route path="/settings" element={<SettingsPage />}>
                <Route path="custom-fields" element={<ModuleGuard moduleKey="settings_custom_fields"><CustomFieldsPage /></ModuleGuard>} />
                <Route path="branches" element={<ModuleGuard moduleKey="employees"><BranchesPage /></ModuleGuard>} />
                <Route path="access" element={<ModuleGuard moduleKey="settings_access_roles"><AccessRolesPage /></ModuleGuard>} />
                <Route path="module-visibility" element={<ModuleGuard moduleKey="settings_module_visibility"><ModuleVisibilityPage /></ModuleGuard>} />
                <Route path="notifications" element={<NotificationsPage />} />
                <Route path="repository" element={<RepositoryPage />} />
                <Route path="ai-inbox" element={<AIInboxPage />} />
                <Route path="letter-templates" element={<LetterTemplatesPage />} />
                <Route path="document-templates" element={<DocumentTemplatesPage />} />
              </Route>
              <Route path="/profile" element={<ProfilePage />} />
            </Route>

            {/* Default redirect */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            
            {/* 404 */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </BrowserRouter>
        
        {/* Toast notifications */}
        <Toaster 
          position="top-right"
          toastOptions={{
            style: {
              background: 'white',
              border: '1px solid #e2e8f0',
            },
          }}
        />
          </ModulePolicyProvider>
        </PermissionProvider>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
